import React from 'react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, AreaChart, Area
} from 'recharts';

// Theme-aware chart colors — work in both light and dark mode
const CHART_THEME = {
  grid: 'rgba(100,116,139,0.15)',
  axis: 'var(--vz-text-muted, #64748B)',
  tooltipBg: 'var(--vz-bg-elevated)',
  tooltipBorder: 'var(--vz-border-subtle)',
  cursor: 'rgba(74,158,255,0.06)',
};

const CustomTooltip = ({ active, payload, label, formatter }) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      className="rounded-xl p-3 text-xs shadow-xl"
      style={{ background: 'var(--vz-bg-elevated)', border: '1px solid var(--vz-border-subtle)' }}
    >
      <p className="font-montserrat font-semibold mb-1" style={{ color: 'var(--vz-text-primary)' }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>
          {p.name}: <span className="font-bold tabular-nums">{formatter ? formatter(p.value) : p.value.toLocaleString('es-ES')}</span>
        </p>
      ))}
    </div>
  );
};

export const TrendLineChart = ({ data = [], dataKey = 'total', color = '#00C853', label = 'Total', height = 200 }) => (
  <ResponsiveContainer width="100%" height={height}>
    <AreaChart data={data} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
      <defs>
        <linearGradient id={`grad-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="5%" stopColor={color} stopOpacity={0.18} />
          <stop offset="95%" stopColor={color} stopOpacity={0.02} />
        </linearGradient>
      </defs>
      <CartesianGrid strokeDasharray="3 8" stroke={CHART_THEME.grid} />
      <XAxis dataKey="periodo" tick={{ fill: CHART_THEME.axis, fontSize: 10 }} tickLine={false} axisLine={false} />
      <YAxis tick={{ fill: CHART_THEME.axis, fontSize: 10 }} tickLine={false} axisLine={false} />
      <Tooltip content={<CustomTooltip />} cursor={{ stroke: CHART_THEME.cursor, strokeWidth: 30, fill: CHART_THEME.cursor }} />
      <Area
        type="monotone"
        dataKey={dataKey}
        stroke={color}
        strokeWidth={2}
        fill={`url(#grad-${color.replace('#', '')})`}
        dot={false}
        activeDot={{ r: 4, fill: color, stroke: 'var(--vz-bg-card)', strokeWidth: 2 }}
        name={label}
      />
    </AreaChart>
  </ResponsiveContainer>
);

export const HorizontalBarChart = ({ data = [], dataKey = 'total', nameKey = 'name', color = '#4A9EFF', height = 250, labelWidth = 185 }) => (
  <ResponsiveContainer width="100%" height={height}>
    <BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, left: 4, bottom: 4 }}>
      <CartesianGrid strokeDasharray="3 8" stroke={CHART_THEME.grid} horizontal={false} />
      <XAxis type="number" tick={{ fill: CHART_THEME.axis, fontSize: 10 }} tickLine={false} axisLine={false} />
      <YAxis
        type="category"
        dataKey={nameKey}
        width={labelWidth}
        tick={{ fill: 'var(--vz-text-muted, #64748B)', fontSize: 10 }}
        tickLine={false}
        axisLine={false}
        tickFormatter={(v) => v?.length > 26 ? v.substring(0, 24) + '…' : v}
      />
      <Tooltip content={<CustomTooltip />} cursor={{ fill: CHART_THEME.cursor }} />
      <Bar dataKey={dataKey} fill={color} radius={[0, 4, 4, 0]} maxBarSize={20} />
    </BarChart>
  </ResponsiveContainer>
);

export const VerticalBarChart = ({ data = [], dataKey = 'total', nameKey = 'name', color = '#00C853', height = 250, incumplimientoKey }) => (
  <ResponsiveContainer width="100%" height={height}>
    <BarChart data={data} margin={{ top: 4, right: 4, left: -20, bottom: 60 }}>
      <CartesianGrid strokeDasharray="3 8" stroke={CHART_THEME.grid} />
      <XAxis
        dataKey={nameKey}
        tick={{ fill: CHART_THEME.axis, fontSize: 9 }}
        tickLine={false}
        axisLine={false}
        angle={-40}
        textAnchor="end"
        interval={0}
        tickFormatter={(v) => v?.length > 12 ? v.substring(0, 10) + '…' : v}
      />
      <YAxis tick={{ fill: CHART_THEME.axis, fontSize: 10 }} tickLine={false} axisLine={false} />
      <Tooltip content={<CustomTooltip />} cursor={{ fill: CHART_THEME.cursor }} />
      <Bar dataKey={dataKey} fill={color} radius={[4, 4, 0, 0]} maxBarSize={32} name="Total" />
      {incumplimientoKey && (
        <Bar dataKey={incumplimientoKey} fill="#FF5252" radius={[4, 4, 0, 0]} maxBarSize={32} name="Incumplimiento" />
      )}
    </BarChart>
  </ResponsiveContainer>
);

const DONUT_COLORS = ['#00C853', '#4A9EFF', '#FFD600', '#FF6B35', '#AB47BC', '#FF5252', '#69F0AE'];

export const DonutChart = ({ data = [], dataKey = 'total', nameKey = 'name', height = 200, colorMap = null }) => (
  <ResponsiveContainer width="100%" height={height}>
    <PieChart>
      <Pie
        data={data}
        cx="50%"
        cy="50%"
        innerRadius={50}
        outerRadius={80}
        paddingAngle={2}
        dataKey={dataKey}
        nameKey={nameKey}
      >
        {data.map((d, i) => {
          // When colorMap is provided we honour name → colour mapping so the
          // donut slice, the legend chip and the label all match (e.g. severity:
          // "Verde" must always be green). Otherwise fall back to the sequential
          // palette.
          const name = (d?.[nameKey] || '').toString();
          const colour = colorMap
            ? (colorMap[name] || colorMap[name.toLowerCase()] || DONUT_COLORS[i % DONUT_COLORS.length])
            : DONUT_COLORS[i % DONUT_COLORS.length];
          return <Cell key={i} fill={colour} />;
        })}
      </Pie>
      <Tooltip content={<CustomTooltip />} />
      <Legend
        formatter={(v) => <span style={{ color: 'var(--vz-text-muted, #64748B)', fontSize: 11 }}>{v?.length > 18 ? v.substring(0, 16) + '…' : v}</span>}
        iconType="circle"
        iconSize={8}
      />
    </PieChart>
  </ResponsiveContainer>
);

export const CHARTS_COLORS = DONUT_COLORS;
